import { useCallback, useEffect, useMemo, useState } from 'react';

import { Add, Close } from '@mui/icons-material';
import { Box, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { StickyHeader } from 'components';
import { AutocompleteFormField, TextFormField } from 'components/FormFields';
import strings from 'constants/index';
import { formatError } from 'utils/request.utils';

import { PermissionsWrapper } from '.';
import { PermissionsModel } from '../Permissions.types';
import { PermissionsActions } from './PermissionsActions';
import { permissionsCardStyles } from './PermissionsCard.styles';
import { Prompt } from 'components/Prompt/Prompt';
import { priorityTypes } from 'utils/table.utils';

interface PermissionsFormProps {
  data: PermissionsModel;
  onFormSubmit: (formData: PermissionsModel) => Promise<void>;
  showIngestionSettings?: boolean;
}

// Lazy: avoids TDZ/circular-init issues when table.utils is still evaluating under Vitest.
const getPriorityOptions = () =>
  priorityTypes.map(item => ({
    id: parseInt(item.name, 10),
    name: item.value,
  }));

const normalizeIngestionDisabledCompetitions = (value?: string[] | null) =>
  Array.from(new Set((value || []).map(item => item.trim()).filter(Boolean)));

const getPriorityOption = (priority: unknown) => {
  const priorityOptions = getPriorityOptions();
  if (priority === null || priority === undefined) {
    return null;
  }

  if (typeof priority === 'number' && !Number.isNaN(priority)) {
    return priorityOptions.find(option => option.id === priority) || null;
  }

  if (typeof priority === 'string') {
    const normalizedPriority = Number.parseInt(priority, 10);

    if (!Number.isNaN(normalizedPriority)) {
      return priorityOptions.find(option => option.id === normalizedPriority) || null;
    }

    return (
      priorityOptions.find(option => option.name.toLowerCase() === priority.trim().toLowerCase()) ||
      null
    );
  }

  if (typeof priority === 'object') {
    const priorityObject = priority as {
      id?: number | string;
      name?: string;
      value?: number | string;
    };

    return (
      getPriorityOption(priorityObject.id) ||
      getPriorityOption(priorityObject.value) ||
      getPriorityOption(priorityObject.name)
    );
  }

  return null;
};

type PermissionsFormValues = PermissionsModel & {
  priorityOption: { id: number; name: string } | null;
};

export const PermissionForm = ({
  data,
  onFormSubmit,
  showIngestionSettings = false,
}: PermissionsFormProps) => {
  const priorityOption = getPriorityOption(data.priority);
  const permissionsForm = useForm<PermissionsFormValues>({
    mode: 'onChange',
    defaultValues: {
      ...data,
      priority: priorityOption?.id ?? 0,
      ingestionDisabledCompetitions: normalizeIngestionDisabledCompetitions(
        data.ingestionDisabledCompetitions,
      ),
      priorityOption,
    },
  });

  const [isAdmin, setIsAdmin] = useState(() => data.isAdmin ?? false);
  const [isReadAdmin, setIsReadAdmin] = useState(() => data.isReadAdmin ?? false);
  const [isIngestionDisabled, setIsIngestionDisabled] = useState(
    () => data.ingestionDisabled ?? false,
  );
  const [competitionInput, setCompetitionInput] = useState('');

  const {
    handleSubmit,
    reset,
    setError,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { isDirty, isValid, errors },
  } = permissionsForm;

  const onSubmit = (values: PermissionsFormValues) => {
    clearErrors();
    const { priorityOption, ...formValues } = values;
    onFormSubmit({
      ...formValues,
      ingestionDisabledCompetitions: normalizeIngestionDisabledCompetitions(
        formValues.ingestionDisabledCompetitions,
      ),
      priority: priorityOption?.id ?? 0,
    }).catch(e => setError('permissions', { type: 'manual', message: formatError(e) }));
  };

  const handleChangeIsAdmin = useCallback(
    (checked: boolean) => {
      setValue('isAdmin', checked, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setIsAdmin(checked);
    },
    [setValue],
  );

  const handleChangeIsReadAdmin = useCallback(
    (checked: boolean) => {
      setValue('isReadAdmin', checked, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setIsReadAdmin(checked);
    },
    [setValue],
  );

  const handleChangeIngestionDisabled = useCallback(
    (checked: boolean) => {
      setValue('ingestionDisabled', checked, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      setIsIngestionDisabled(checked);
    },
    [setValue],
  );

  const handleChangeMinDiffBetweenFixtures = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue('minimumDifferenceBetweenFixtures', parseInt(event.target.value, 10), {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleChangeMatchingFixturesRange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue('matchingFixturesRange', parseInt(event.target.value, 10), {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleAddDisabledCompetition = useCallback(() => {
    const trimmedCompetition = competitionInput.trim();

    if (!trimmedCompetition) {
      return;
    }

    const competitions = normalizeIngestionDisabledCompetitions(
      getValues('ingestionDisabledCompetitions'),
    );

    if (competitions.includes(trimmedCompetition)) {
      setCompetitionInput('');
      return;
    }

    setValue(
      'ingestionDisabledCompetitions',
      [trimmedCompetition, ...competitions],
      {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      },
    );
    setCompetitionInput('');
  }, [competitionInput, getValues, setValue]);

  const handleCompetitionInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddDisabledCompetition();
      }
    },
    [handleAddDisabledCompetition],
  );

  const handleRemoveDisabledCompetition = useCallback(
    (competition: string) => {
      setValue(
        'ingestionDisabledCompetitions',
        normalizeIngestionDisabledCompetitions(
          getValues('ingestionDisabledCompetitions'),
        ).filter(item => item !== competition),
        {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true,
        },
      );
    },
    [getValues, setValue],
  );

  const handleReset = useCallback(() => {
    reset();
    setIsAdmin(data.isAdmin ?? false);
    setIsReadAdmin(data.isReadAdmin ?? false);
    setIsIngestionDisabled(data.ingestionDisabled ?? false);
    setCompetitionInput('');
  }, [data, reset]);

  const selectedPriorityOption = watch('priorityOption');
  const ingestionDisabledCompetitions = watch('ingestionDisabledCompetitions');
  const disabledCompetitions = useMemo(
    () => normalizeIngestionDisabledCompetitions(ingestionDisabledCompetitions),
    [ingestionDisabledCompetitions],
  );

  useEffect(() => {
    const resolvedPriorityOption = getPriorityOption(data.priority);
    const currentPriorityOption = getValues('priorityOption');
    const nextPriorityOption = resolvedPriorityOption || currentPriorityOption || null;

    permissionsForm.reset({
      ...data,
      priority: nextPriorityOption?.id ?? 0,
      ingestionDisabledCompetitions: normalizeIngestionDisabledCompetitions(
        data.ingestionDisabledCompetitions,
      ),
      priorityOption: nextPriorityOption,
    });
  }, [data, getValues, permissionsForm]);

  useEffect(() => {
    const selectedPriorityId = selectedPriorityOption?.id ?? 0;

    if (getValues('priority') === selectedPriorityId) {
      return;
    }

    setValue('priority', selectedPriorityId, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });
  }, [getValues, selectedPriorityOption, setValue]);

  useEffect(() => {
    setIsAdmin(data.isAdmin ?? false);
    setIsReadAdmin(data.isReadAdmin ?? false);
    setIsIngestionDisabled(data.ingestionDisabled ?? false);
  }, [data]);

  return (
    <FormProvider {...permissionsForm}>
      <Prompt when={isDirty} message={strings.unsaved_changes} />
      <StickyHeader
        titleType={strings.edit_permissions}
        titleId={data.username}
        titleName={data.tenant}
      >
        <PermissionsActions
          isResetDisabled={!isDirty}
          isSaveDisabled={!isDirty || !isValid || Object.keys(errors).length > 0}
          onReset={handleReset}
          onSubmit={handleSubmit(onSubmit)}
        />
      </StickyHeader>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        sx={permissionsCardStyles.adminCard}
      >
        <Box display="flex" flexWrap="wrap" gap="8px">
          <FormControlLabel
            control={<Switch checked={isAdmin} onChange={() => handleChangeIsAdmin(!isAdmin)} />}
            label={strings.role_curator}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isReadAdmin}
                onChange={() => handleChangeIsReadAdmin(!isReadAdmin)}
              />
            }
            label={strings.role_viewer}
            labelPlacement="start"
          />
          {showIngestionSettings && (
            <FormControlLabel
              control={
                <Switch
                  checked={isIngestionDisabled}
                  onChange={() => handleChangeIngestionDisabled(!isIngestionDisabled)}
                />
              }
              label={strings.ingestionDisabled}
              labelPlacement="start"
            />
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          gap="15px"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography>{strings.minimumDifferenceBetweenFixtures}</Typography>
          <Box width="15%">
            <TextFormField
              type="number"
              name="minimumDifferenceBetweenFixtures"
              onChange={handleChangeMinDiffBetweenFixtures}
            />
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          gap="15px"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography>{strings.matchingFixturesRange}</Typography>
          <Box width="15%">
            <TextFormField
              type="number"
              name="matchingFixturesRange"
              onChange={handleChangeMatchingFixturesRange}
            />
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          gap="15px"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography>{strings.priority}</Typography>
          <Box width="25%">
            <AutocompleteFormField
              name="priorityOption"
              dataSource={getPriorityOptions()}
              disableClearable
              hideId
              noOrder
              noHeader
            />
          </Box>
        </Box>
        {showIngestionSettings && (
          <Box display="flex" flexDirection="column" gap="8px" width="100%">
            <Typography>{strings.ingestionDisabledCompetitions}</Typography>
            <Box display="flex" flexDirection="row" gap="8px" alignItems="flex-end" width="100%">
              <TextField
                data-testid="ingestionDisabledCompetitionInput"
                label={strings.ingestionDisabledCompetitionInput}
                onChange={event => setCompetitionInput(event.target.value)}
                onKeyDown={handleCompetitionInputKeyDown}
                value={competitionInput}
                variant="standard"
                fullWidth
              />
              <IconButton
                aria-label={`${strings.add} ${strings.ingestionDisabledCompetitionInput}`}
                disabled={!competitionInput.trim()}
                onClick={handleAddDisabledCompetition}
                title={`${strings.add} ${strings.ingestionDisabledCompetitionInput}`}
              >
                <Add />
              </IconButton>
            </Box>
            <Box
              data-testid="ingestionDisabledCompetitions"
              border="1px solid rgba(0, 0, 0, 0.23)"
              borderRadius="4px"
              maxHeight="120px"
              minHeight="80px"
              overflow="auto"
              width="100%"
            >
              {disabledCompetitions.map(competition => (
                <Box
                  key={competition}
                  alignItems="center"
                  display="flex"
                  justifyContent="space-between"
                  padding="4px 8px"
                >
                  <Typography>{competition}</Typography>
                  <IconButton
                    aria-label={`${strings.remove} ${competition}`}
                    onClick={() => handleRemoveDisabledCompetition(competition)}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <PermissionsWrapper prefix="permissions" sx={permissionsCardStyles.cardWrapper} />
    </FormProvider>
  );
};
